import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import { onAuthChange, signInWithGoogle, signOut } from "./services/auth";
import { FIREBASE_ENABLED, REQUIRE_AUTH } from "./config/env";

interface AuthState {
  user: User | null;
  loading: boolean;
  // Whether the app should gate on sign-in. False against a local backend with
  // STUDIO_AUTH_DISABLED.
  authRequired: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(FIREBASE_ENABLED);

  useEffect(() => {
    const unsub = onAuthChange((u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      authRequired: REQUIRE_AUTH && FIREBASE_ENABLED,
      signIn: async () => {
        await signInWithGoogle();
      },
      signOut,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
